import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Mail, Lock, Loader2, ArrowRight, Eye, EyeOff, ShieldCheck } from 'lucide-react';
import { login as doLogin, useAuth } from '../lib/authStore';

export default function Login() {
  const { isAuthenticated } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const emailRef = useRef(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const next = (() => {
    const sp = new URLSearchParams(loc.search);
    const raw = sp.get('next');
    return raw && raw.startsWith('/') ? raw : '/';
  })();

  useEffect(() => {
    if (isAuthenticated) nav(next, { replace: true });
  }, [isAuthenticated, nav, next]);

  useEffect(() => { emailRef.current?.focus(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError('');
    setLoading(true);
    try {
      const user = await doLogin(email.trim().toLowerCase(), password);
      // First-login flow: LO just used an admin-issued temp password.
      // Route them to /set-password no matter where they were headed —
      // ProtectedRoute would catch this anyway but doing it here avoids
      // the intermediate flash of the target screen.
      if (user?.must_change_password) {
        nav('/set-password', { replace: true });
      } else {
        nav(next, { replace: true });
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not sign in. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Champagne palette — keep in sync across halo, button, accents.
  const GOLD       = '#e8cc8c';
  const GOLD_BRIGHT = '#f5e1b0';
  const GOLD_DEEP  = '#b89867';

  return (
    <div
      data-testid="login-page"
      className="relative min-h-screen overflow-hidden flex items-center justify-center px-4 py-10"
      style={{
        background:
          'radial-gradient(ellipse 80% 60% at 50% 0%, #0c0c0d 0%, #060606 60%, #030303 100%)',
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Inter", "Helvetica Neue", system-ui, sans-serif',
      }}
    >
      {/* ============================================================
         Atmospheric layers — built bottom-up so depth feels real.
         ============================================================ */}
      {/* (1) Floor — subtle warm horizon + textured matte sheen */}
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-[36%]"
        style={{
          background:
            'linear-gradient(to top, rgba(232,204,140,0.06) 0%, rgba(232,204,140,0.02) 30%, transparent 80%)',
        }}
      />
      {/* (2) Floor reflection — narrow elliptical wash directly under the halo */}
      <div
        className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-[6%] w-[680px] h-[60px] rounded-[50%]"
        style={{
          background: `radial-gradient(ellipse at center, ${GOLD}33 0%, ${GOLD}10 40%, transparent 70%)`,
          filter: 'blur(6px)',
        }}
      />
      {/* (3) The halo — a precise gold arc using SVG for crisp edges */}
      <Halo gold={GOLD} goldBright={GOLD_BRIGHT} />
      {/* (4) Vignette — pull eyes toward the center */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 80% 70% at 50% 50%, transparent 50%, rgba(0,0,0,0.55) 90%, rgba(0,0,0,0.85) 100%)',
        }}
      />
      {/* (5) Film grain — barely-there texture so matte black feels organic */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.06] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
        }}
      />

      {/* ============================================================
         The composition — logo, headline, card.
         ============================================================ */}
      <div className="relative z-10 w-full max-w-[440px] flex flex-col items-center">
        {/* Brand wordmark — Kinfolk, masked + filled with champagne gold (same tone as the tagline) */}
        <div
          aria-label="Kinfolk"
          className="select-none"
          style={{
            width: '320px',
            height: '64px',
            backgroundColor: GOLD,
            opacity: 0.55,
            WebkitMaskImage: 'url(/brand/kinfolk-wordmark-only.png)',
            maskImage: 'url(/brand/kinfolk-wordmark-only.png)',
            WebkitMaskRepeat: 'no-repeat',
            maskRepeat: 'no-repeat',
            WebkitMaskPosition: 'center',
            maskPosition: 'center',
            WebkitMaskSize: 'contain',
            maskSize: 'contain',
            filter: `drop-shadow(0 0 14px ${GOLD}22)`,
          }}
        />

        {/* Tagline — editorial serif italic, hairline gold flanks */}
        <div className="flex items-center gap-4 mt-5 mb-8">
          <span
            className="h-px w-10"
            style={{
              background: `linear-gradient(to right, transparent, ${GOLD}88)`,
            }}
          />
          <span
            className="text-[9px] uppercase whitespace-nowrap"
            style={{
              fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "SF Pro Display", system-ui, sans-serif',
              color: GOLD,
              opacity: 0.55,
              fontWeight: 300,
              letterSpacing: '0.32em',
            }}
          >
            Sign in to continue to your dashboard
          </span>
          <span
            className="h-px w-10"
            style={{
              background: `linear-gradient(to left, transparent, ${GOLD}88)`,
            }}
          />
        </div>

        {/* ===== Card ===== */}
        <form
          onSubmit={submit}
          data-testid="login-form"
          autoComplete="off"
          className="w-full rounded-[22px] p-7"
          style={{
            background: 'linear-gradient(180deg, rgba(20,20,20,0.78) 0%, rgba(10,10,10,0.78) 100%)',
            backdropFilter: 'blur(18px) saturate(140%)',
            WebkitBackdropFilter: 'blur(18px) saturate(140%)',
            border: `1px solid ${GOLD}22`,
            boxShadow:
              '0 30px 60px -20px rgba(0,0,0,0.75), 0 0 0 1px rgba(255,255,255,0.02) inset, 0 1px 0 rgba(255,255,255,0.05) inset',
          }}
        >
          {/* Email */}
          <LuxField
            Icon={Mail}
            placeholder="Email address"
            type="email"
            value={email}
            onChange={setEmail}
            inputRef={emailRef}
            autoComplete="username"
            testid="login-email"
            gold={GOLD}
          />

          {/* Password */}
          <div className="mt-3.5">
            <LuxField
              Icon={Lock}
              placeholder="Password"
              type={showPwd ? 'text' : 'password'}
              value={password}
              onChange={setPassword}
              autoComplete="current-password"
              testid="login-password"
              gold={GOLD}
              trailing={(
                <button
                  type="button"
                  onClick={() => setShowPwd((s) => !s)}
                  tabIndex={-1}
                  data-testid="login-toggle-password"
                  aria-label={showPwd ? 'Hide password' : 'Show password'}
                  className="h-9 w-9 rounded-md flex items-center justify-center text-neutral-500 hover:text-neutral-200 transition"
                >
                  {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              )}
            />
          </div>

          {/* Remember + Forgot */}
          <div className="flex items-center justify-between mt-5">
            <label className="inline-flex items-center gap-2.5 cursor-pointer select-none" data-testid="login-remember">
              <span className="relative inline-flex items-center justify-center">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  className="appearance-none h-[15px] w-[15px] rounded-[4px] border border-white/15 bg-transparent cursor-pointer transition focus:outline-none"
                  style={remember ? { background: GOLD, borderColor: GOLD } : undefined}
                />
                {remember && (
                  <svg className="absolute h-2.5 w-2.5 pointer-events-none" viewBox="0 0 10 10" fill="none">
                    <path d="M1 5l2.8 2.8L9 2" stroke="#080808" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </span>
              <span className="text-[12.5px]" style={{ color: '#9a9a9a' }}>Remember me</span>
            </label>
            <a
              href="#"
              data-testid="login-forgot"
              onClick={(e) => { e.preventDefault(); setError('Contact your administrator to reset your password.'); }}
              className="text-[12.5px] transition"
              style={{ color: GOLD }}
            >
              Forgot password?
            </a>
          </div>

          {error && (
            <div
              data-testid="login-error"
              className="mt-4 text-[12px] font-medium rounded-lg px-3 py-2"
              style={{ color: '#ffb4ad', background: 'rgba(244,93,72,0.08)', border: '1px solid rgba(244,93,72,0.25)' }}
            >
              {error}
            </div>
          )}

          {/* Sign-in — champagne gradient hero button */}
          <button
            type="submit"
            disabled={loading || !email || !password}
            data-testid="login-submit"
            className="group mt-6 w-full h-[54px] rounded-[14px] text-[14.5px] font-medium inline-flex items-center justify-center gap-3 disabled:opacity-55 disabled:cursor-not-allowed transition-all"
            style={{
              background: `linear-gradient(180deg, ${GOLD_BRIGHT} 0%, ${GOLD} 55%, ${GOLD_DEEP} 100%)`,
              color: '#1a1408',
              boxShadow:
                `0 1px 0 ${GOLD_BRIGHT} inset, 0 -1px 0 ${GOLD_DEEP}88 inset, 0 12px 28px -10px ${GOLD}66, 0 2px 6px rgba(0,0,0,0.4)`,
              border: '1px solid rgba(255,255,255,0.18)',
              letterSpacing: '0.01em',
            }}
          >
            {loading
              ? (<><Loader2 className="h-4 w-4 animate-spin" /> Signing in…</>)
              : (
                <>
                  <span>Sign in</span>
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={2.2} />
                </>
              )
            }
          </button>
        </form>
      </div>
    </div>
  );
}

/* =========================================================================
   Halo — SVG-based arc for sharp edges + layered glow
   ========================================================================= */
function Halo({ gold, goldBright }) {
  return (
    <svg
      className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-[52%]"
      width="1100"
      height="1100"
      viewBox="0 0 1100 1100"
      aria-hidden
      style={{ maxWidth: '120vw', maxHeight: '120vh' }}
    >
      <defs>
        {/* Fade the bottom half of the ring so it reads as an arc, not a full circle */}
        <linearGradient id="haloFade" x1="0.5" y1="0" x2="0.5" y2="1">
          <stop offset="0%"  stopColor="#fff" stopOpacity="1" />
          <stop offset="50%" stopColor="#fff" stopOpacity="1" />
          <stop offset="78%" stopColor="#fff" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
        <mask id="haloMask" maskUnits="userSpaceOnUse" x="0" y="0" width="1100" height="1100">
          <rect width="1100" height="1100" fill="url(#haloFade)" />
        </mask>
        <filter id="haloBlurOuter" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="18" />
        </filter>
        <filter id="haloBlurInner" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="5" />
        </filter>
      </defs>

      {/* (a) Wide outer atmospheric glow */}
      <circle
        cx="550" cy="550" r="430"
        fill="none"
        stroke={gold}
        strokeOpacity="0.18"
        strokeWidth="55"
        mask="url(#haloMask)"
        filter="url(#haloBlurOuter)"
      />
      {/* (b) Medium glow halo */}
      <circle
        cx="550" cy="550" r="430"
        fill="none"
        stroke={gold}
        strokeOpacity="0.45"
        strokeWidth="14"
        mask="url(#haloMask)"
        filter="url(#haloBlurInner)"
      />
      {/* (c) Crisp inner ring — the eclipse edge */}
      <circle
        cx="550" cy="550" r="430"
        fill="none"
        stroke={goldBright}
        strokeOpacity="0.95"
        strokeWidth="1.6"
        mask="url(#haloMask)"
      />
    </svg>
  );
}

/* =========================================================================
   LuxField — borderless dark input with icon + optional trailing slot
   ========================================================================= */
function LuxField({ Icon, placeholder, type, value, onChange, autoComplete, inputRef, testid, trailing, gold }) {
  const [focused, setFocused] = useState(false);
  return (
    <div
      className="relative h-[54px] rounded-[14px] transition-all"
      style={{
        background: 'rgba(0,0,0,0.45)',
        border: `1px solid ${focused ? `${gold}55` : 'rgba(255,255,255,0.08)'}`,
        boxShadow: focused ? `0 0 0 3px ${gold}10` : undefined,
      }}
    >
      <Icon
        className="absolute left-[18px] top-1/2 -translate-y-1/2 pointer-events-none"
        style={{ color: focused ? '#cfcfcf' : '#666' }}
        strokeWidth={1.6}
        width={17}
        height={17}
      />
      <input
        ref={inputRef}
        type={type}
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        className="w-full h-full bg-transparent pl-[46px] pr-12 text-[13px] text-white placeholder:font-normal focus:outline-none"
        style={{ fontWeight: 300, letterSpacing: '0.005em', color: '#ffffff' }}
      />
      {trailing && (
        <span className="absolute right-2 top-1/2 -translate-y-1/2">{trailing}</span>
      )}
    </div>
  );
}
